import dayjs, { Dayjs } from "dayjs";
import {
  FORM2PAYLOAD_INITIAL_VALUE,
  type Form2Payload,
} from "@/pages/app/provider/scheduling/appointment/encounter/encounter-constant";
import type { Option } from "@/constants/common";
import { PROVIDER_PATHS } from "@/constants/paths";
import {
  APPOINTMENT_MODE_LABEL,
  GROUP_APPOINTMENT_STATUS,
  GROUP_APPOINTMENT_STATUS_LABEL,
  SESSION_ID_QUERY_PARAM,
  type GroupSessionDetail,
  type GroupSessionPatient,
} from "@/pages/app/provider/scheduling/group-appointment";
import {
  AssessmentCode,
  EncounterSumary,
  EncounterSumaryEncounterStatusEnum,
  GetAllGroupVisitsGroupStatusEnum,
  GroupVisit,
  GroupVisitSharedNote,
} from "@/services";
import {
  ARCHIVABLE_STATUSES,
  ATTENDANCE_STATUS,
  CHECK_IN_CLOSED_SESSION_STATUSES,
  CHECK_IN_PAYMENT_STATUS,
  PARTICIPANT_NOTE_STATE,
  GROUP_ACTIVITY_OPTIONS,
  GROUP_NOTE_DEFAULT_TEMPLATES,
  GROUP_SESSION_PRINT_DATE_FORMAT,
  GROUP_NOTE_TEMPLATE_LABEL,
  GROUP_TYPE_NOTE_TEMPLATES,
  SESSION_DOCUMENTATION_LOCKED_STATUSES,
  SESSION_INFO_EMPTY_VALUE,
  SESSION_INFO_FIELD,
  SESSION_NOTE_CO_THERAPIST_SUFFIX,
  SESSION_NOTE_HEADER_SEPARATOR,
  SESSION_NOTE_INITIAL_VALUES,
  THERAPEUTIC_INTERVENTION_SECTIONS,
  YES_NO_LABEL,
  SESSION_NOTE_TIME_FORMAT,
  SESSION_NOTE_TIME_RANGE_SEPARATOR,
  CHECK_IN_ROW_ACTION,
  FINALIZE_CHECK_STATE,
  FINALIZE_SESSION_LABEL,
  NOTE_REQUIRED_STATUSES,
  GROUP_LIST_FILTER,
  GROUP_FIELDS_INITIAL_VALUES,
  GROUP_FORM_INITIAL_VALUES,
  GROUP_MIN_TO_RUN_MIN,
  GROUP_RECURRING_DEFAULTS,
  RECURRING_END_TYPE,
  RECURRING_FREQUENCY,
  SESSION_FREQUENCY_TO_RECURRING,
  GROUP_ROW_ACTION,
  GROUP_ROW_ACTION_HINT,
  GROUP_ROW_ACTION_LABEL,
  GROUP_STATUS_TOGGLE_LABEL,
  GROUP_DETAIL_DEFAULT_TAB,
  GROUP_DETAIL_TAB_VALUES,
  GROUP_STATUS,
  GROUP_STATUS_VALUES,
  GROUP_UNCONFIGURED_VALUE,
  PROVIDER_NAME_SEPARATOR,
  PATIENT_ENROLLMENT_STATUS,
  SESSION_STATUS,
  NOTIFICATION_CHANNEL_FIELD,
  NOTIFICATION_SECTION,
  NOTIFICATION_SETTINGS_INITIAL_VALUES,
  REMINDER_SCHEDULE_FIELD,
} from "./group-visit-constant";
import {
  AttendanceStatus,
  AvailablePatientRow,
  CheckInPatient,
  CheckInPaymentStatus,
  CheckInRowAction,
  FinalizeCheck,
  GroupDetail,
  GroupDetailTab,
  GroupNextSession,
  GroupPatient,
  GroupTherapist,
  GroupTherapistRow,
  GroupVisitPatientRow,
  PatientEnrollmentStatus,
  GroupFormValues,
  GroupListFilter,
  GroupPayload,
  GroupStatus,
  GroupVisitListRow,
  SessionFrequency,
  GroupFieldsValues,
  GroupSessionPayload,
  GroupRecurrencePayload,
  GroupRecurrenceValues,
  GroupSessionRecurrence,
  GroupNotificationField,
  GroupNotificationSettings,
  NotificationSection,
  GroupNoteTemplate,
  GroupSessionPrintData,
  GroupType,
  ParticipantNoteState,
  SavedEncounterNote,
  SessionCheckboxItem,
  SessionNoteValues,
  SessionDocumentationHeader,
  SessionParticipant,
} from "./group-visit-types";

const API_DATE_FORMAT = "YYYY-MM-DD";

const toInputValue = (value?: number | null) =>
  value === undefined || value === null ? "" : String(value);

const toNumber = (value: string) => (value === "" ? null : Number(value));

/**
 * The API still emits the retired DRAFT status; anything outside the current
 * set stays empty instead of being read as Inactive.
 */
const toGroupStatus = (status?: string | null): GroupStatus | undefined =>
  GROUP_STATUS_VALUES.includes(status as GroupStatus)
    ? (status as GroupStatus)
    : undefined;

/**
 * Maps a saved group onto the controlled form shape, filling gaps with defaults.
 *
 * @author Rajkumar Rathod
 */
export const toGroupFormValues = (group?: GroupDetail): GroupFormValues => {
  if (!group) return GROUP_FORM_INITIAL_VALUES;

  return {
    ...GROUP_FORM_INITIAL_VALUES,
    groupName: group.groupName ?? "",
    groupType: group.groupType ?? null,
    therapyCategory: group.therapyCategory ?? null,
    amountForPatient: toInputValue(group.amountForPatient),
    description: group.description ?? "",
    status: toGroupStatus(group.status) ?? GROUP_FORM_INITIAL_VALUES.status,

    sessionDurationMinutes: group.sessionDurationMinutes ?? null,
    sessionFrequency: (group.sessionFrequency as SessionFrequency) ?? null,
    customDays: group.customDays ?? [],
    maxCapacity: toInputValue(group.maxCapacity),
    minToRun: toInputValue(group.minToRun ?? GROUP_MIN_TO_RUN_MIN),

    primaryTherapist: group.primaryTherapistId
      ? {
          value: group.primaryTherapistId,
          label: group.primaryTherapistName ?? "",
        }
      : null,
    coTherapist: group.coTherapistId
      ? { value: group.coTherapistId, label: group.coTherapistName ?? "" }
      : null,

    startDate: group.startDate ? dayjs(group.startDate) : null,
    ...toRecurrenceValues(group),
  };
};

/** Saved recurrence onto the form; a missing key keeps its default. */
const toRecurrenceValues = (
  group: Partial<GroupPayload>
): GroupRecurrenceValues => ({
  isRecurring: group.isRecurring ?? false,
  recurringFrequency:
    group.recurringFrequency ?? GROUP_RECURRING_DEFAULTS.recurringFrequency,
  endType: group.endType ?? GROUP_RECURRING_DEFAULTS.endType,
  endDate: group.endDate ? dayjs(group.endDate) : null,
  endAfter: group.endAfter ?? GROUP_RECURRING_DEFAULTS.endAfter,
  customFrequency:
    group.customFrequency ?? GROUP_RECURRING_DEFAULTS.customFrequency,
  customFrequencyUnit:
    group.customFrequencyUnit ?? GROUP_RECURRING_DEFAULTS.customFrequencyUnit,
  selectedWeekdays: group.selectedWeekdays ?? [],
});

/**
 * Recurrence keys for the API: `endDate` only for an On-date end, `endAfter`
 * only for an After-N end, and the custom keys only for a custom frequency.
 */
const toRecurrencePayload = (
  values: GroupRecurrenceValues
): GroupSessionRecurrence => {
  const isCustom = values.recurringFrequency === RECURRING_FREQUENCY.CUSTOM;
  const endsOnDate = values.endType === RECURRING_END_TYPE.ON_DATE;

  return {
    isRecurring: values.isRecurring,
    recurringFrequency: values.recurringFrequency,
    endType: values.endType,
    endDate: endsOnDate ? values.endDate?.toISOString() : undefined,
    endAfter: endsOnDate ? undefined : values.endAfter,
    customFrequency: isCustom ? values.customFrequency : undefined,
    customFrequencyUnit: isCustom ? values.customFrequencyUnit : undefined,
    selectedWeekdays: isCustom ? values.selectedWeekdays : undefined,
  };
};

export const toGroupPayload = (values: GroupFormValues): GroupPayload => ({
  groupName: values.groupName.trim(),
  groupType: values.groupType,
  therapyCategory: values.therapyCategory,
  amountForPatient: toNumber(values.amountForPatient),
  description: values.description.trim() || null,
  status: values.status,
  sessionDurationMinutes: values.sessionDurationMinutes,
  sessionFrequency: values.sessionFrequency,
  customDays: values.customDays,
  maxCapacity: toNumber(values.maxCapacity),
  minToRun: toNumber(values.minToRun),
  primaryTherapistId: values.primaryTherapist?.value ?? null,
  coTherapistId: values.coTherapist?.value ?? null,
  startDate: values.startDate?.format(API_DATE_FORMAT) ?? null,
  ...toRecurrencePayload(values),
  endDate: values.endDate?.format(API_DATE_FORMAT) ?? null,
});

/**
 * Splits a list tab into its query params. Archived is a flag on the record,
 * not one of the group statuses the API accepts.
 */
export const toGroupListParams = (filter: GroupListFilter) => {
  if (filter === GROUP_LIST_FILTER.ARCHIVED)
    return { groupStatus: undefined, archive: true };

  if (filter === GROUP_LIST_FILTER.ALL)
    return { groupStatus: undefined, archive: undefined };

  return {
    groupStatus: filter as GetAllGroupVisitsGroupStatusEnum,
    archive: false,
  };
};

const toRowNumber = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Maps a list row onto `GroupDetail`. The archive flag is folded into the
 * status so the chip and row actions read one field.
 *
 * @author Rajkumar Rathod
 */
export const toGroupDetail = (row: GroupVisitListRow): GroupDetail => ({
  uuid: row.uuid,
  groupName: row.groupName,
  primaryTherapistName: row.primaryTherapistName ?? undefined,
  coTherapistName: row.coTherapistName ?? undefined,
  groupType: row.groupType ?? null,
  therapyCategory: row.therapyCategory ?? null,
  enrolledPatientCount: row.enrolledPatients ?? 0,
  maxCapacity: toRowNumber(row.maximumCapacity),
  minToRun: toRowNumber(row.minimumToRun),
  status: row.archive ? GROUP_STATUS.ARCHIVED : toGroupStatus(row.groupStatus),
});

/**
 * Saved group from the detail API onto the internal detail shape the form
 * reads. The archive flag is folded into the status, as in the list.
 *
 * @author Rajkumar Rathod
 */
export const toGroupDetailFromGroupVisit = (
  groupVisit: GroupVisit
): GroupDetail => ({
  uuid: groupVisit.uuid,
  groupName: groupVisit.groupName,
  groupType: groupVisit.groupType ?? null,
  therapyCategory: groupVisit.therapyCategory ?? null,
  amountForPatient: toRowNumber(groupVisit.amountForPatient),
  description: groupVisit.groupDescription ?? null,
  status: groupVisit.archive
    ? GROUP_STATUS.ARCHIVED
    : toGroupStatus(groupVisit.groupStatus),

  sessionDurationMinutes: groupVisit.defaultSessionDuration ?? null,
  sessionFrequency: (groupVisit.sessionFrequency as SessionFrequency) ?? null,
  maxCapacity: toRowNumber(groupVisit.maximumCapacity),
  minToRun: toRowNumber(groupVisit.minimumToRun),

  primaryTherapistId: groupVisit.primaryTherapist ?? null,
  primaryTherapistName: groupVisit.primaryTherapistName ?? undefined,
  coTherapistId: groupVisit.coTherapist ?? null,
  coTherapistName: groupVisit.coTherapistName ?? undefined,

  startDate: groupVisit.startDate ?? null,
  endDate: groupVisit.endDate ?? null,
  isRecurring: groupVisit.recurringSchedule ?? false,
  // Recurrence keys land on `GroupVisit` once the SDK is regenerated.
  ...pickRecurrence(groupVisit as GroupVisit & Partial<GroupRecurrencePayload>),
  // `nextSession` lands on `GroupVisit` once the SDK is regenerated.
  nextSession:
    (groupVisit as GroupVisit & { nextSession?: GroupNextSession | null })
      .nextSession ?? undefined,
});

const pickRecurrence = ({
  recurringFrequency,
  endType,
  endAfter,
  customFrequency,
  customFrequencyUnit,
  selectedWeekdays,
}: Partial<GroupRecurrencePayload>): Partial<GroupRecurrencePayload> => ({
  recurringFrequency,
  endType,
  endAfter,
  customFrequency,
  customFrequencyUnit,
  selectedWeekdays,
});

/** Saved record without its recurrence keys, so the update never sends them. */
const omitRecurrence = ({
  recurringSchedule: _recurringSchedule,
  recurringFrequency: _recurringFrequency,
  endType: _endType,
  endDate: _endDate,
  endAfter: _endAfter,
  customFrequency: _customFrequency,
  customFrequencyUnit: _customFrequencyUnit,
  selectedWeekdays: _selectedWeekdays,
  ...rest
}: GroupVisit): GroupVisit => rest;

/**
 * Update payload for `PUT /api/master/group-visit`. The saved record is spread
 * first because the API rewrites every field — an omitted boolean saves as
 * `false`, so the untouched sections have to travel with the edits.
 *
 * @author Rajkumar Rathod
 */
export const toGroupVisitPayload = (
  values: GroupFormValues,
  saved: GroupVisit
): GroupVisit => ({
  ...omitRecurrence(saved),
  groupName: values.groupName.trim(),
  groupType: values.groupType ?? "",
  therapyCategory: values.therapyCategory ?? "",
  amountForPatient: values.amountForPatient || undefined,
  groupDescription: values.description,

  defaultSessionDuration:
    values.sessionDurationMinutes ?? saved.defaultSessionDuration,
  sessionFrequency: values.sessionFrequency ?? undefined,
  maximumCapacity: values.maxCapacity || undefined,
  minimumToRun: values.minToRun || undefined,

  primaryTherapist: values.primaryTherapist?.value ?? undefined,
  coTherapist: values.coTherapist?.value ?? undefined,

  startDate: values.startDate?.toISOString() ?? undefined,
  // repeatsOn: undefined,
  // ...toRecurrencePayload(values),
  // recurringSchedule: values.isRecurring,
  groupStatus: GROUP_STATUS.ACTIVE,
});

/** Route of the Edit Group Details screen for a group. */
export const getGroupEditPath = (groupVisitId: string) =>
  `${PROVIDER_PATHS.GROUP_VISIT}/${groupVisitId}/edit`;

export const getGroupViewPath = (groupVisitId: string) =>
  `${PROVIDER_PATHS.GROUP_VISIT}/${groupVisitId}/view`;

/** Uuid of the group a create call returned, read from the response envelope's `data`. */
export const getCreatedGroupId = (response: unknown): string | undefined => {
  if (typeof response !== "object" || !response || !("data" in response)) {
    return undefined;
  }
  const envelope = response.data;
  if (typeof envelope !== "object" || !envelope || !("data" in envelope)) {
    return undefined;
  }
  return typeof envelope.data === "string" && envelope.data
    ? envelope.data
    : undefined;
};

/** Route of a group's detail screen, on the given tab. */
export const getGroupDetailPath = (
  groupVisitId: string,
  tab: string = GROUP_DETAIL_DEFAULT_TAB
) => `${PROVIDER_PATHS.GROUP_VISIT}/${groupVisitId}/${tab}`;

export const isGroupDetailTab = (tab?: string): tab is GroupDetailTab =>
  !!tab && GROUP_DETAIL_TAB_VALUES.includes(tab);

/** Status chip variant for the group list and the form header. */
export const getGroupStatusVariant = (status?: string | null) => {
  if (status === GROUP_STATUS.ACTIVE) return "success" as const;
  if (status === GROUP_STATUS.ARCHIVED) return "neutral" as const;
  if (status === GROUP_STATUS.INACTIVE) return "warning" as const;
  return "default" as const;
};

export const canArchiveGroup = (status?: string | null) =>
  !!status && ARCHIVABLE_STATUSES.includes(status);

export const isGroupArchived = (status?: string | null) =>
  status === GROUP_STATUS.ARCHIVED;

/** Status the Change Status action flips a group to: Active ↔ Inactive. */
export const getToggledGroupStatus = (status?: string | null) =>
  status === GROUP_STATUS.ACTIVE ? GROUP_STATUS.INACTIVE : GROUP_STATUS.ACTIVE;

/**
 * Row actions for a group, with the ones its status forbids disabled and
 * carrying the reason as a tooltip.
 */
export const getGroupRowActions = (status?: string | null) => {
  const archived = isGroupArchived(status);

  return [
    {
      value: GROUP_ROW_ACTION.VIEW_DETAILS,
      label: GROUP_ROW_ACTION_LABEL[GROUP_ROW_ACTION.VIEW_DETAILS],
    },
    // An archived group has no visits left to complete.
    ...(archived
      ? []
      : [
          {
            value: GROUP_ROW_ACTION.VIEW,
            label: GROUP_ROW_ACTION_LABEL[GROUP_ROW_ACTION.VIEW],
          },
        ]),
    {
      value: GROUP_ROW_ACTION.EDIT,
      label: GROUP_ROW_ACTION_LABEL[GROUP_ROW_ACTION.EDIT],
      isDisabled: archived,
      hoverMsg: GROUP_ROW_ACTION_HINT.archivedReadOnly,
    },
    {
      value: GROUP_ROW_ACTION.CHANGE_STATUS,
      label: GROUP_STATUS_TOGGLE_LABEL[getToggledGroupStatus(status)],
      isDisabled: archived,
      hoverMsg: GROUP_ROW_ACTION_HINT.archivedReadOnly,
    },
    {
      value: GROUP_ROW_ACTION.ARCHIVE,
      label: GROUP_ROW_ACTION_LABEL[GROUP_ROW_ACTION.ARCHIVE],
      isDisabled: !canArchiveGroup(status),
      hoverMsg: GROUP_ROW_ACTION_HINT.archiveUnavailable,
    },
  ];
};

/** Enrolled / capacity as "7/10", or "-" until the group has a capacity set. */
export const formatEnrolledPatients = (
  enrolled?: number | null,
  capacity?: number | null
) => (capacity ? `${enrolled ?? 0}/${capacity}` : GROUP_UNCONFIGURED_VALUE);

/** Provider Name cell — primary first, then the co-therapist when assigned. */
export const formatProviderNames = (
  primaryTherapistName?: string | null,
  coTherapistName?: string | null
) =>
  [primaryTherapistName, coTherapistName]
    .filter(Boolean)
    .join(PROVIDER_NAME_SEPARATOR) || GROUP_UNCONFIGURED_VALUE;

export const formatMinToRun = (minToRun?: number | null) =>
  minToRun ? String(minToRun) : GROUP_UNCONFIGURED_VALUE;

const DETAIL_SEPARATOR = " · ";
const LIST_SEPARATOR = ", ";

/** Joins present parts with the middot separator used across the details cards. */
export const joinDetailParts = (parts: (string | null | undefined)[]) =>
  parts.filter(Boolean).join(DETAIL_SEPARATOR);

export const getOptionLabel = (
  options: { value: string; label: string }[],
  value?: string | null
) => options.find((option) => option.value === value)?.label;

const MAX_INITIALS = 2;
const FULL_PERCENT = 100;

export const getInitials = (name: string) =>
  name
    .split(" ")
    .filter((part) => /^[A-Za-z]/.test(part))
    .slice(0, MAX_INITIALS)
    .map((part) => part[0].toUpperCase())
    .join("");

export const getEnrollmentPercent = (enrolled: number, capacity: number) =>
  capacity > 0
    ? Math.min((enrolled / capacity) * FULL_PERCENT, FULL_PERCENT)
    : 0;

/** Status chip variant for a scheduled group session. */
export const getSessionStatusVariant = (status?: string | null) => {
  if (status === SESSION_STATUS.SCHEDULED) return "success" as const;
  if (status === SESSION_STATUS.CANCELLED) return "error" as const;
  return "default" as const;
};

/** Status chip variant for a patient's enrollment in a group. */
export const getPatientStatusVariant = (status?: string | null) => {
  if (status === PATIENT_ENROLLMENT_STATUS.ACTIVE) return "success" as const;
  if (status === PATIENT_ENROLLMENT_STATUS.PENDING) return "pending" as const;
  if (status === PATIENT_ENROLLMENT_STATUS.ON_HOLD) return "warning" as const;
  return "info" as const;
};

/** API row onto the roster shape the Manage Patients table renders. */
export const toGroupPatient = (row: GroupVisitPatientRow): GroupPatient => ({
  uuid: row.uuid ?? "",
  patientId: row.patientId ?? undefined,
  name: row.patientName ?? "",
  mrn: row.mrn,
  birthDate: row.birthDate ?? undefined,
  paymentMode: row.paymentMode ?? undefined,
  enrolledAt: row.enrolledDate ?? undefined,
  status:
    (row.enrollmentStatus as PatientEnrollmentStatus) ??
    PATIENT_ENROLLMENT_STATUS.ACTIVE,
  attendedSessions: row.attendedSessions ?? undefined,
  totalSessions: row.totalSessions ?? undefined,
});

/** Available patient onto the Add Patient drawer's option shape. */
export const toPatientOption = (row: AvailablePatientRow) => ({
  id: row.uuid ?? "",
  label: row.name ?? "",
});

/** Joins an id → name map into one display string; undefined when empty. */
const joinNamedMap = (map?: Record<string, string> | null) => {
  const names = Object.values(map ?? {}).filter(Boolean);
  return names.length ? names.join(LIST_SEPARATOR) : undefined;
};

/** Therapist row onto the shape the care team and profile cards render. */
export const toGroupTherapist = (row: GroupTherapistRow): GroupTherapist => ({
  uuid: row.uuid ?? "",
  name: row.name ?? "",
  role: row.role ?? undefined,
  email: row.email ?? undefined,
  specialty: joinNamedMap(row.specialities),
  location: joinNamedMap(row.workLocations),
});

/** Status chip variant for a patient's session payment. */
export const getCheckInStatusVariant = (status?: CheckInPaymentStatus) =>
  status === CHECK_IN_PAYMENT_STATUS.PAID
    ? ("success" as const)
    : ("warning" as const);

/** A due payment can be linked; a paid one has no action. */
export const getCheckInRowActions = (
  status?: CheckInPaymentStatus
): CheckInRowAction[] =>
  status === CHECK_IN_PAYMENT_STATUS.DUE
    ? [
        CHECK_IN_ROW_ACTION.COLLECT_PAYMENT,
        CHECK_IN_ROW_ACTION.SEND_PAYMENT_LINK,
      ]
    : [];

export const canStartSession = (status?: string) =>
  !!status && !CHECK_IN_CLOSED_SESSION_STATUSES.includes(status);

/** Only attendees who turned up owe an individual note. */
export const isNoteRequired = (status?: string | null) =>
  !!status && NOTE_REQUIRED_STATUSES.includes(status);

export const isSessionDocumentationLocked = (status?: string | null) =>
  !!status && SESSION_DOCUMENTATION_LOCKED_STATUSES.includes(status);

export const withSessionParam = (path: string, sessionId: string) =>
  `${path}?${SESSION_ID_QUERY_PARAM}=${sessionId}`;

export const isEncounterSignedOff = (
  summary?: EncounterSumary | SavedEncounterNote
) =>
  !!summary &&
  (!!summary.providerSignature ||
    summary.encounterStatus === EncounterSumaryEncounterStatusEnum.Completed);

export const getParticipantNoteState = (
  summary?: EncounterSumary | SavedEncounterNote
): ParticipantNoteState => {
  if (!summary) return PARTICIPANT_NOTE_STATE.NOT_STARTED;
  return isEncounterSignedOff(summary)
    ? PARTICIPANT_NOTE_STATE.COMPLETE
    : PARTICIPANT_NOTE_STATE.IN_PROGRESS;
};

/**
 * Attendance is not stored, so it is read back from the notes: a closed session
 * marks anyone without a completed note No-show, and an open one leaves
 * attendance unmarked until a note is started.
 */
const getAttendanceStatus = (
  noteState: ParticipantNoteState,
  isLocked: boolean
): AttendanceStatus | null => {
  if (isLocked)
    return noteState === PARTICIPANT_NOTE_STATE.COMPLETE
      ? ATTENDANCE_STATUS.PRESENT
      : ATTENDANCE_STATUS.NO_SHOW;
  return noteState === PARTICIPANT_NOTE_STATE.NOT_STARTED
    ? null
    : ATTENDANCE_STATUS.PRESENT;
};

export const toSessionParticipant = (
  patient: GroupSessionPatient,
  summary?: SavedEncounterNote,
  isLocked = false
): SessionParticipant => {
  const noteState = getParticipantNoteState(summary);

  return {
    uuid: patient.appointmentId,
    patientUuid: patient.patientId,
    name: patient.patientName,
    patientId: patient.mrn ?? undefined,
    status: getAttendanceStatus(noteState, isLocked),
    noteState,
  };
};

const toTherapistsLine = (session: GroupSessionDetail) =>
  [
    session.primaryTherapistName ?? session.therapistName,
    session.coTherapistName
      ? `${session.coTherapistName} ${SESSION_NOTE_CO_THERAPIST_SUFFIX}`
      : undefined,
  ]
    .filter(Boolean)
    .join(", ");

export const toSessionDocumentationHeader = (
  session: GroupSessionDetail,
  group?: GroupVisit
): SessionDocumentationHeader => ({
  groupName: session.groupName,
  sessionInfo: group?.groupDescription ?? undefined,
  therapyType: session.groupType ?? session.therapyCategory ?? undefined,
  startsAt: session.startTime,
  timeRange: session.endTime
    ? [session.startTime, session.endTime]
        .map((time) => dayjs(time).format(SESSION_NOTE_TIME_FORMAT))
        .join(SESSION_NOTE_TIME_RANGE_SEPARATOR)
    : dayjs(session.startTime).format(SESSION_NOTE_TIME_FORMAT),
  therapists: toTherapistsLine(session) || undefined,
});

/** Templates the group's type supports, and the one opened by default. */
export const getGroupNoteTemplates = (
  groupType?: string | null
): GroupNoteTemplate[] =>
  GROUP_TYPE_NOTE_TEMPLATES[groupType as GroupType] ??
  GROUP_NOTE_DEFAULT_TEMPLATES;

export const getGroupNoteTemplateOptions = (groupType?: string | null) =>
  getGroupNoteTemplates(groupType).map((template) => ({
    value: template,
    label: GROUP_NOTE_TEMPLATE_LABEL[template],
  }));

/**
 * The template a note opens on: the one already saved when the group still
 * supports it, otherwise the first the group's type offers.
 */
export const getGroupNoteTemplate = (
  groupType?: string | null,
  savedTemplate?: string | null
): GroupNoteTemplate => {
  const templates = getGroupNoteTemplates(groupType);
  return (
    templates.find((template) => template === savedTemplate) ?? templates[0]
  );
};

/** The shared note as the form holds it, read back from the saved record. */
export const toSessionNoteValues = (
  note?: GroupVisitSharedNote
): SessionNoteValues => ({
  ...SESSION_NOTE_INITIAL_VALUES,
  ...note,
  groupActivities: (note?.groupActivities ?? []).flatMap(
    (value) =>
      GROUP_ACTIVITY_OPTIONS.find((option) => option.value === value) ?? []
  ),
});

/** The shared note as the API stores it: activities as their option values. */
export const toGroupNotePayload = (
  values: SessionNoteValues
): GroupVisitSharedNote => ({
  ...values,
  groupActivities: values.groupActivities.map((activity) => activity.value),
});

/** Checkbox sections persist as a comma-separated string of option names. */
export const parseSelectedNames = (value?: string): string[] =>
  typeof value === "string" ? value.split(",").filter(Boolean) : [];

/**
 * The section's selection after an option is toggled, rebuilt in the canonical
 * item order so the stored string stays stable.
 */
export const toggleSelectedName = (
  items: SessionCheckboxItem[],
  selected: string[],
  name: string,
  checked: boolean
): string =>
  items
    .map((item) => item.name)
    .filter((itemName) =>
      itemName === name ? checked : selected.includes(itemName)
    )
    .join(",");

/** The read-only Session Information rows, keyed by field. */
export const toSessionInfoValues = (
  header: SessionDocumentationHeader,
  dateFormat: string
): Record<string, string> => ({
  [SESSION_INFO_FIELD.GROUP_NAME]: header.groupName || SESSION_INFO_EMPTY_VALUE,
  [SESSION_INFO_FIELD.SESSION_INFO]:
    header.sessionInfo || SESSION_INFO_EMPTY_VALUE,
  [SESSION_INFO_FIELD.GROUP_TYPE]:
    header.therapyType || SESSION_INFO_EMPTY_VALUE,
  [SESSION_INFO_FIELD.PROVIDERS]: header.therapists || SESSION_INFO_EMPTY_VALUE,
  [SESSION_INFO_FIELD.DATE_TIME]:
    [
      header.startsAt ? dayjs(header.startsAt).format(dateFormat) : undefined,
      header.timeRange,
    ]
      .filter(Boolean)
      .join(SESSION_NOTE_HEADER_SEPARATOR) || SESSION_INFO_EMPTY_VALUE,
});

/**
 * The shared note print, mapped from the session detail and the saved note.
 * Option keys are resolved to their labels so the PDF reads as the form does.
 *
 * @author Rajkumar Rathod
 */
export const toGroupSessionPrintData = (
  session: GroupSessionDetail | undefined,
  header: SessionDocumentationHeader,
  values: SessionNoteValues,
  /** The signing therapist's stored signature, once the session is signed. */
  providerSignature?: string
): GroupSessionPrintData => {
  const selectedInterventions = parseSelectedNames(
    values.therapeuticInterventions
  );

  return {
    groupName: header.groupName || SESSION_INFO_EMPTY_VALUE,
    sessionInfo: header.sessionInfo || SESSION_INFO_EMPTY_VALUE,
    groupType: session?.groupType || SESSION_INFO_EMPTY_VALUE,
    therapyCategory: session?.therapyCategory || SESSION_INFO_EMPTY_VALUE,
    dateTime:
      [
        header.startsAt
          ? dayjs(header.startsAt).format(GROUP_SESSION_PRINT_DATE_FORMAT)
          : undefined,
        header.timeRange,
      ]
        .filter(Boolean)
        .join(SESSION_NOTE_HEADER_SEPARATOR) || SESSION_INFO_EMPTY_VALUE,
    mode: session?.mode
      ? (APPOINTMENT_MODE_LABEL[session.mode] ?? session.mode)
      : SESSION_INFO_EMPTY_VALUE,
    room: session?.room || SESSION_INFO_EMPTY_VALUE,
    status: session?.sessionStatus
      ? (GROUP_APPOINTMENT_STATUS_LABEL[session.sessionStatus] ??
        session.sessionStatus)
      : SESSION_INFO_EMPTY_VALUE,
    therapists: header.therapists || SESSION_INFO_EMPTY_VALUE,
    attendeeCount: session?.patients?.length ?? 0,
    participants: (session?.patients ?? []).map((patient) => ({
      name: patient.patientName || SESSION_INFO_EMPTY_VALUE,
      mrn: patient.mrn || SESSION_INFO_EMPTY_VALUE,
      birthDate: patient.birthDate
        ? dayjs(patient.birthDate).format(GROUP_SESSION_PRINT_DATE_FORMAT)
        : SESSION_INFO_EMPTY_VALUE,
      email: patient.email || SESSION_INFO_EMPTY_VALUE,
    })),
    sessionObjectives: values.sessionObjectives,
    topicsCovered: values.topicsCovered,
    therapeuticInterventions: THERAPEUTIC_INTERVENTION_SECTIONS.flatMap(
      (section) =>
        section.items
          .filter((item) => selectedInterventions.includes(item.name))
          .map((item) => item.label)
    ),
    therapeuticInterventionsNote: values.therapeuticInterventionsNote,
    groupActivities: values.groupActivities.map((activity) => activity.label),
    homeworkAssigned: values.isHomeworkAssigned
      ? YES_NO_LABEL.yes
      : YES_NO_LABEL.no,
    isHomeworkAssigned: values.isHomeworkAssigned,
    homework: values.homework,
    overallGroupSummary: values.overallGroupSummary,
    signedBy:
      session?.primaryTherapistName ||
      session?.therapistName ||
      SESSION_INFO_EMPTY_VALUE,
    signedOnDate: session?.startTime
      ? dayjs(session.startTime).format(GROUP_SESSION_PRINT_DATE_FORMAT)
      : SESSION_INFO_EMPTY_VALUE,
    signedOnTime: session?.startTime
      ? dayjs(session.startTime).format(SESSION_NOTE_TIME_FORMAT)
      : SESSION_INFO_EMPTY_VALUE,
    providerSignature,
    isSigned: session?.sessionStatus === GROUP_APPOINTMENT_STATUS.COMPLETED,
  };
};

type IndividualNoteContext = {
  participant: SessionParticipant;
  providerId?: string;
  /** The saved record, when this note is updating one. */
  summary?: SavedEncounterNote;
  template?: GroupNoteTemplate;
};

type NoteCodes = NonNullable<Form2Payload["assessmentCodes"]>;

/** Snapshot codes as the ICD/CPT pickers hold them: `code - description`. */
const toNoteCodes = (codes?: Form2Payload["assessmentCodes"]): NoteCodes =>
  ((codes ?? []) as AssessmentCode[]).map((code) => ({
    value: code.uuid ?? "",
    code: `${code.code} - ${code.description || ""}`,
  }));

/** The note as the form holds it, read back from the encounter summary. */
export const toIndividualNoteValues = (
  summary?: SavedEncounterNote,
  template?: GroupNoteTemplate
): Form2Payload => ({
  ...FORM2PAYLOAD_INITIAL_VALUE,
  ...summary,
  ...(summary && {
    assessmentCodes: toNoteCodes(summary.assessmentCodes),
    procedureCodes: toNoteCodes(summary.procedureCodes),
  }),
  formType: template ?? summary?.formType,
});

export const toIndividualNotePayload = (
  values: Form2Payload,
  { participant, providerId, summary, template }: IndividualNoteContext
): EncounterSumary => ({
  ...(values as unknown as EncounterSumary),
  uuid: summary?.uuid,
  appointmentId: participant.uuid,
  patientId: participant.patientUuid,
  providerId,
  formType: template,
  // Sign-off rejects a note without one, and the group note has no CC field.
  chiefComplaint:
    values.chiefComplaint ||
    (template ? GROUP_NOTE_TEMPLATE_LABEL[template] : undefined),
});

type FinalizeReadiness = {
  totalParticipants: number;
  markedParticipants: number;
  attendeeCount: number;
  isGroupNoteComplete: boolean;
  completedNotes: number;
  requiredNotes: number;
};

/** Readiness rows for the finalize dialog, in the order it lists them. */
export const getFinalizeChecks = ({
  totalParticipants,
  markedParticipants,
  isGroupNoteComplete,
  completedNotes,
  requiredNotes,
}: FinalizeReadiness): FinalizeCheck[] => [
  {
    id: "attendance",
    label: FINALIZE_SESSION_LABEL.attendanceRecorded,
    state:
      markedParticipants >= totalParticipants
        ? FINALIZE_CHECK_STATE.COMPLETE
        : FINALIZE_CHECK_STATE.PARTIAL,
    statusLabel: `${markedParticipants} / ${totalParticipants}`,
  },
  {
    id: "group-note",
    label: FINALIZE_SESSION_LABEL.sharedGroupNote,
    state: isGroupNoteComplete
      ? FINALIZE_CHECK_STATE.COMPLETE
      : FINALIZE_CHECK_STATE.PARTIAL,
    statusLabel: isGroupNoteComplete
      ? FINALIZE_SESSION_LABEL.complete
      : FINALIZE_SESSION_LABEL.draft,
  },
  {
    id: "individual-notes",
    label: FINALIZE_SESSION_LABEL.individualNotes,
    state:
      completedNotes >= requiredNotes
        ? FINALIZE_CHECK_STATE.COMPLETE
        : FINALIZE_CHECK_STATE.PARTIAL,
    statusLabel: `${completedNotes} / ${totalParticipants}`,
  },
];

/** The dialog's one-line account of what is and is not done. */
export const getFinalizeSummary = ({
  totalParticipants,
  markedParticipants,
  attendeeCount,
  isGroupNoteComplete,
  completedNotes,
}: FinalizeReadiness) =>
  [
    `${attendeeCount} of ${totalParticipants} patients attended this session.`,
    markedParticipants < totalParticipants
      ? `Attendance not marked for ${totalParticipants - markedParticipants}.`
      : "",
    isGroupNoteComplete
      ? "Shared group note completed."
      : "Shared group note still in draft.",
    `${completedNotes} of ${totalParticipants} individual notes completed.`,
  ]
    .filter(Boolean)
    .join(" ");

/** Duration options are labelled ("30 minutes"); the API takes the number. */
export const toDurationMinutes = (value: string | null): number | undefined => {
  const minutes = parseInt(value ?? "", 10);
  return Number.isNaN(minutes) ? undefined : minutes;
};

/** Primary then co-therapist; the slots API intersects their availability. */
export const getGroupTherapistIds = (group?: GroupDetail): string[] =>
  [group?.primaryTherapistId, group?.coTherapistId].filter(
    (id): id is string => !!id
  );

const joinOptionValues = (options: Option[]) =>
  options.length ? options.map((option) => option.value).join(",") : undefined;

/** Maps the Schedule Appointment (Group) form onto the schedule-session API. */
export const toGroupSessionPayload = (
  values: GroupFieldsValues
): GroupSessionPayload => ({
  groupVisitId: values.groupId ?? "",
  session: {
    startTime: values.startTime ?? "",
    duration: toDurationMinutes(values.sessionDurationMinutes),
    providerId: values.providerId ?? undefined,
    mode: values.mode,
    locationId: values.locationId?.value,
    ...toRecurrencePayload(values),
    sendForms: values.sendForms,
    customForms: values.sendForms
      ? joinOptionValues(values.customForms)
      : undefined,
    assessments: values.sendForms
      ? joinOptionValues(values.assessments)
      : undefined,
  },
});

const toDayjsOrNull = (value?: string | null) => (value ? dayjs(value) : null);

// Sessions cannot be booked in the past, so a group that has already started schedules from today.
const toScheduleStartDate = (groupStartDate?: string | null): Dayjs => {
  const today = dayjs().startOf("day");
  const groupStart = toDayjsOrNull(groupStartDate);
  return groupStart?.isAfter(today, "day") ? groupStart : today;
};

const toRecurringFrequency = (sessionFrequency?: string | null) =>
  SESSION_FREQUENCY_TO_RECURRING[sessionFrequency as SessionFrequency] ??
  GROUP_RECURRING_DEFAULTS.recurringFrequency;

/** Prefills the Schedule Appointment (Group) form from the chosen group's record. */
export const toGroupFieldsValues = (
  group: GroupVisit,
  groupId: string
): GroupFieldsValues => {
  return {
    ...GROUP_FIELDS_INITIAL_VALUES,
    groupId,
    startDate: toScheduleStartDate(group.startDate),
    sessionDurationMinutes: group.defaultSessionDuration ?? null,
    providerId: group.primaryTherapist ?? null,
    isRecurring: group.recurringSchedule ?? false,
    recurringFrequency: toRecurringFrequency(group.sessionFrequency),
    endDate: toDayjsOrNull(group.endDate),
  };
};

const readNotificationFields = (
  group: GroupVisit,
  fields: Record<string, GroupNotificationField>
): Record<string, boolean> =>
  Object.fromEntries(
    Object.entries(fields).map(([id, field]) => [id, group[field] ?? false])
  );

/** The six `GroupVisit` notification booleans onto the toggle sections. */
export const toGroupNotificationSettings = (
  group: GroupVisit
): GroupNotificationSettings => ({
  channels: readNotificationFields(group, NOTIFICATION_CHANNEL_FIELD),
  reminders: readNotificationFields(group, REMINDER_SCHEDULE_FIELD),
  templateId: NOTIFICATION_SETTINGS_INITIAL_VALUES.templateId,
});

/** The `GroupVisit` boolean behind one toggle; undefined for an unknown id. */
export const getNotificationField = (
  section: NotificationSection,
  id: string
): GroupNotificationField | undefined =>
  section === NOTIFICATION_SECTION.CHANNELS
    ? NOTIFICATION_CHANNEL_FIELD[id as keyof typeof NOTIFICATION_CHANNEL_FIELD]
    : REMINDER_SCHEDULE_FIELD[id as keyof typeof REMINDER_SCHEDULE_FIELD];

/** Maps one session roster row onto the check-in table. */
export const toCheckInPatient = (
  patient: GroupSessionPatient
): CheckInPatient => ({
  uuid: patient.appointmentId,
  patientId: patient.patientId,
  name: patient.patientName,
  mrn: patient.mrn ?? undefined,
  email: patient.email,
  amount: patient.amount,
  paymentStatus: patient.paid
    ? CHECK_IN_PAYMENT_STATUS.PAID
    : CHECK_IN_PAYMENT_STATUS.DUE,
  paymentLinkSent: patient.paymentLinkSent,
});
